import { IonContent } from '@ionic/react';
import { Button, cn, ghostAccentIconTileClass } from '@up2stay/up2promo-ui';
import { motion, useReducedMotion } from 'motion/react';
import {
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  Package,
  Star,
  UserCircle,
} from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { LogoutConfirmDialog } from '@/core/components/logout-confirm-dialog';
import { useLogout } from '@/core/hooks/use-logout';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useDictionary } from '@/core/providers/dictionary-provider';

import {
  WORKSPACE_INTENT,
  WORKSPACE_INTENTS,
  type WorkspaceIntent,
} from './onboarding-intent';
import { AppIonPage } from '@/core/components/app-ion-page';

const PILLAR_ICONS: Record<WorkspaceIntent, LucideIcon> = {
  [WORKSPACE_INTENT.profiles]: UserCircle,
  [WORKSPACE_INTENT.touchpoints]: Package,
  [WORKSPACE_INTENT.reputation]: Star,
  [WORKSPACE_INTENT.full]: LayoutDashboard,
};

const OnboardingHeaderLeadingContext = createContext<
  ((node: ReactNode) => void) | null
>(null);

/** Publish a leading header action (e.g. Back) into {@link OnboardingLayout}. */
export function useOnboardingHeaderLeading() {
  return useContext(OnboardingHeaderLeadingContext);
}

interface OnboardingLayoutProps {
  children: ReactNode;
}

/**
 * Chrome for the mandatory `/onboarding` page: explanatory brand rail on
 * desktop, single header row (Back leading · Logout trailing).
 */
export function OnboardingLayout({ children }: OnboardingLayoutProps) {
  const isMobile = useIsMobile();
  const dict = useDictionary();
  const { dialogOpen, setDialogOpen, requestLogout, confirmLogout } =
    useLogout();
  const [leading, setLeadingState] = useState<ReactNode>(null);

  const setLeading = useCallback((node: ReactNode) => {
    setLeadingState(node);
  }, []);

  const leadingContextValue = useMemo(() => setLeading, [setLeading]);

  return (
    <AppIonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="flex h-dvh w-full overflow-hidden bg-background text-foreground">
          {!isMobile && <BrandPanel dict={dict} />}

          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-80"
              style={{
                backgroundImage:
                  'radial-gradient(90% 55% at 50% -10%, color-mix(in oklab, var(--brand-500) 8%, transparent), transparent 70%)',
              }}
            />

            <header
              className={cn(
                'relative z-20 flex shrink-0 items-center justify-between gap-3',
                'px-4 pb-2 md:px-8',
              )}
              style={{
                paddingTop: isMobile
                  ? 'calc(env(safe-area-inset-top, 0px) + 0.5rem)'
                  : '1rem',
              }}
            >
              <div className="flex min-w-0 items-center">{leading}</div>
              <Button
                variant="ghost"
                size={isMobile ? 'md' : 'sm'}
                onClick={requestLogout}
                className="shrink-0 text-muted-foreground"
              >
                <LogOut className="h-4 w-4" />
                {dict.onboarding.logout}
              </Button>
            </header>

            <OnboardingHeaderLeadingContext.Provider
              value={leadingContextValue}
            >
              <div className="relative z-10 flex min-h-0 flex-1 flex-col">
                {children}
              </div>
            </OnboardingHeaderLeadingContext.Provider>
          </div>
        </div>

        <LogoutConfirmDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onConfirm={confirmLogout}
        />
      </IonContent>
    </AppIonPage>
  );
}

function BrandPanel({ dict }: { dict: ReturnType<typeof useDictionary> }) {
  const reduceMotion = useReducedMotion();
  const panel = dict.onboarding.brandPanel;
  const options = dict.onboarding.intent.options;

  return (
    <div className="relative hidden w-[min(40%,28rem)] flex-col justify-between border-r border-border/60 bg-sidebar p-8 lg:flex xl:p-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(80% 50% at 20% 0%, color-mix(in oklab, var(--brand-500) 10%, transparent), transparent 65%)',
        }}
      />

      <div className="relative z-10">
        <img
          src="/assets/images/brand/banner.svg"
          alt="up2promo"
          className="h-8 dark:invert"
        />
      </div>

      <div className="relative z-10 my-8 flex flex-1 flex-col justify-center gap-6">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md flex flex-col gap-2"
        >
          <p className="text-balance text-[15.5px] font-bold leading-snug tracking-tight text-foreground">
            {panel.title}
          </p>
          <p className="text-pretty text-[13.5px] leading-relaxed text-muted-foreground">
            {panel.description}
          </p>
        </motion.div>

        <ul className="flex max-w-md flex-col gap-3">
          {WORKSPACE_INTENTS.map((intent, index) => {
            const Icon = PILLAR_ICONS[intent];
            const item = options[intent];
            return (
              <motion.li
                key={intent}
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: reduceMotion ? 0 : 0.06 + index * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="flex gap-3"
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                    ghostAccentIconTileClass,
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 self-center text-[13.5px] font-semibold text-foreground">
                  {item.label}
                </span>
              </motion.li>
            );
          })}
        </ul>

        <p className="max-w-md text-[13.5px] leading-relaxed text-muted-foreground">
          {panel.footnote}
        </p>
      </div>

      <p className="relative z-10 text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        &copy; {new Date().getFullYear()} up2stay GmbH
      </p>
    </div>
  );
}
